/**
 * `useAuth` — Zustand auth store (T-007).
 *
 * Single source of truth for the auth slots, the user profile and the
 * resolved auth mode (`cloud` / `local-only` / `unauthenticated`).
 * Owns:
 *
 *   - JWT lifecycle (set on login, rotate on refresh, clear on logout)
 *   - Refresh-token persistence (cloud path only)
 *   - The anonymous `device_id` that identifies an **account-less Free**
 *     install (F-348 / ADR-002 Amendment 2026-05-28 §A.1). `local-only`
 *     mode now means "device_id, no cloud account, no JWT" — it no
 *     longer holds a locally-issued JWT.
 *   - Active-token resolution (`getActiveJwt()` — used by the API
 *     clients' bearer resolvers; account-less requests carry the
 *     `device_id` as a header instead, see `gateway-client.ts`)
 *   - `exp` extraction from the active JWT (no signature verification
 *     — the client trusts the server here; per ADR-006 F-300 owns
 *     real JWT validation via JWKS)
 *
 * Writes through to `tokenStorage` on every setter so non-React readers
 * (router loaders, `getStoredJwt()` legacy helper used by `AuthGuard`
 * during Wave A, the bearer-token resolvers in `gateway-client.ts` /
 * `accounts-client.ts`) keep observing the same values without
 * subscribing to the store.
 *
 * Refresh orchestration is intentionally NOT inside this file — it
 * imports the store (one-way dep). See
 * `src/features/auth/refreshOrchestrator.ts` and `initAuth()` below.
 *
 * Per F-310 functional spec § 8 and technical spec § 4 / § 6.4 / § 6.5.
 */
import { create } from "zustand";

import type { Tier } from "./accounts";
import { eventBus } from "./eventBus";
import { tokenStorage } from "../features/auth/tokenStorage";

/**
 * Slim user shape stored in the auth state. Matches the fields A4 / A5
 * / shell account chip read; richer profile data (timestamps, etc.)
 * is fetched on demand from `AccountsService.getMe()`.
 */
export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
}

export type AuthMode = "cloud" | "local-only" | "unauthenticated";

export interface AuthState {
  jwt: string | null;
  refresh: string | null;
  deviceId: string | null;
  user: AuthUser | null;
  tier: Tier | null;
  mode: AuthMode;

  /**
   * F-230: ID of the currently-active Mirlai OS instance (cloud-side
   * Device.id UUID). `null` until the first instance is loaded or when
   * there are no paired instances. Set automatically to the first active
   * instance when `useInstances` resolves; updated by the instance
   * switcher UI when the user explicitly picks a different home.
   */
  activeInstanceId: string | null;

  /** F-230: Set the active instance and persist cross-tab. */
  setActiveInstanceId: (id: string | null) => void;

  /**
   * Persist a cloud session: cloud JWT + refresh token + user profile +
   * tier. Replaces any previously stored cloud session in place.
   *
   * NOTE: writes through to localStorage immediately so the next
   * synchronous `getStoredJwt()` (used by `AuthGuard` redirects) sees
   * the new value.
   */
  setCloudSession(jwt: string, refresh: string, user: AuthUser, tier: Tier): void;

  /**
   * Record this install as **account-less Free** (F-348): no cloud
   * account, no JWT — identified only by the anonymous `device_id`
   * (UUID generated by the add-on at first boot, fetched via
   * `deviceId.ts`). Resolves `mode` to `local-only`. The user can later
   * pair / sign up (→ `cloud`); the `device_id` is then carried into
   * `POST /me/attach-account` so the backend adopts it.
   */
  setAccountLess(deviceId: string): void;

  /** Wipe every slot + clear persisted storage. */
  clear(): void;

  /** Cloud JWT or `null`. The `device_id` is NOT a bearer — it travels
   * as the `X-Mirlai-Device-Id` header on local endpoints instead. */
  getActiveJwt(): string | null;

  /** The anonymous account-less `device_id`, present in `local-only`
   * (and carried through `cloud` after pairing), else `null`. */
  getDeviceId(): string | null;

  /**
   * Decode the active JWT's payload and return the `exp` claim (seconds
   * since epoch). Returns `null` when no token is set, when the token
   * is structurally malformed, or when `exp` is missing / not a number.
   */
  getTokenExpiry(): number | null;
}

/**
 * Resolve the auth mode from whatever is sitting in storage.
 * Cloud (JWT) beats account-less (device_id) beats unauthenticated.
 */
function resolveMode(jwt: string | null, deviceId: string | null): AuthMode {
  if (jwt) return "cloud";
  if (deviceId) return "local-only";
  return "unauthenticated";
}

/**
 * URL-safe base64 decode. Pure JS so the auth store has zero deps
 * beyond Zustand.
 *
 * Tolerates both padded and unpadded inputs; returns `null` on any
 * structural failure (caller treats `null` as "no exp", which is
 * the safe choice — refresh orchestrator skips scheduling).
 */
function decodeBase64Url(segment: string): string | null {
  try {
    // RFC 4648 §5: '-' → '+', '_' → '/', then re-pad to 4-byte boundary.
    let normalised = segment.replace(/-/g, "+").replace(/_/g, "/");
    while (normalised.length % 4 !== 0) {
      normalised += "=";
    }
    if (typeof atob === "function") {
      return atob(normalised);
    }
    // Node fallback — `Buffer` is only available outside the browser.
    if (typeof Buffer !== "undefined") {
      return Buffer.from(normalised, "base64").toString("binary");
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract the `exp` claim from a JWT (seconds since epoch). Does NOT
 * verify the signature — only F-300 is allowed to do that, via JWKS.
 * Returns `null` for malformed / signature-only tokens.
 */
export function getJwtExpiry(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const payloadRaw = decodeBase64Url(parts[1] ?? "");
  if (!payloadRaw) return null;
  try {
    const payload = JSON.parse(payloadRaw) as unknown;
    if (payload && typeof payload === "object" && "exp" in payload) {
      const exp = (payload as { exp: unknown }).exp;
      if (typeof exp === "number" && Number.isFinite(exp)) {
        return exp;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export const useAuth = create<AuthState>((set, get) => ({
  // Initial state is empty; `initAuth()` hydrates from storage at boot.
  // Tests that exercise the store in isolation start from this baseline,
  // which is the right default for unit-level coverage.
  jwt: null,
  refresh: null,
  deviceId: null,
  user: null,
  tier: null,
  mode: "unauthenticated",
  // F-230: no instance selected until useInstances resolves.
  activeInstanceId: null,

  setActiveInstanceId(id) {
    set({ activeInstanceId: id });
  },

  setCloudSession(jwt, refresh, user, tier) {
    tokenStorage.write("jwt", jwt);
    tokenStorage.write("refresh", refresh);
    set(() => ({
      jwt,
      refresh,
      user,
      tier,
      // Cloud JWT wins regardless of device_id (which we keep so the
      // post-pair attach + telemetry identity survive the transition).
      mode: resolveMode(jwt, get().deviceId),
    }));
  },

  setAccountLess(deviceId) {
    tokenStorage.write("deviceId", deviceId);
    set((state) => ({
      deviceId,
      mode: resolveMode(state.jwt, deviceId),
    }));
  },

  clear() {
    tokenStorage.clearAll();
    set({
      jwt: null,
      refresh: null,
      deviceId: null,
      user: null,
      tier: null,
      mode: "unauthenticated",
    });
  },

  getActiveJwt() {
    return get().jwt ?? null;
  },

  getDeviceId() {
    return get().deviceId ?? null;
  },

  getTokenExpiry() {
    const active = get().getActiveJwt();
    if (!active) return null;
    return getJwtExpiry(active);
  },
}));

/**
 * Hydrate the store from localStorage. Pure side-effect — does NOT
 * touch the refresh orchestrator (which lives in
 * `features/auth/refreshOrchestrator.ts` to avoid a circular module
 * import). Callers wire the orchestrator separately; see `initAuth()`
 * in `auth-store-init.ts` (the convenience entry-point wired into
 * `main.tsx` by the orchestrator).
 *
 * Idempotent — safe to call more than once. Re-hydrating overwrites
 * the in-memory state with whatever is in storage, which is the
 * correct behaviour for hot-reload during dev.
 *
 * Note: we deliberately drop `user` + `tier` between sessions. Onboarding
 * resume fetches them on demand via `AccountsService.getMe()` (T-018);
 * carrying a stale tier across reloads would risk the UI lying about
 * gating.
 */
export function hydrateAuthFromStorage(): void {
  const jwt = tokenStorage.read("jwt");
  const refresh = tokenStorage.read("refresh");
  const deviceId = tokenStorage.read("deviceId");

  useAuth.setState({
    jwt,
    refresh,
    deviceId,
    user: null,
    tier: null,
    mode: resolveMode(jwt, deviceId),
  });
}

/**
 * Test-only helpers — production code MUST NOT depend on these. The
 * `__internal` namespace mirrors the convention used by `gateway-client.ts`
 * + `accounts-client.ts`.
 */
export const __internal = {
  getJwtExpiry,
  resolveMode,
  decodeBase64Url,
  eventBus,
};
